/**
 * WebSocket Hook
 * CF Workers DO protocol: per-asset /ws/{asset} + /ws/alerts
 * First-message auth: auth_required → {type:'auth', token} → auth_ack
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAlertStore, type Alert, type Trade, type Asset } from '@/stores/alert-store';
import { useAuthStore } from '@/stores/auth-store';

// ============================================================================
// CONSTANTS
// ============================================================================

const PING_INTERVAL = 30_000;
const BACKOFF_BASE = 1_000;
const BACKOFF_MAX = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

// ============================================================================
// TYPES
// ============================================================================

type WsStatus = 'idle' | 'connecting' | 'authenticating' | 'connected' | 'error';

interface WsConnection {
  ws: WebSocket;
  status: WsStatus;
  pingInterval: ReturnType<typeof setInterval> | null;
  reconnectTimeout: ReturnType<typeof setTimeout> | null;
  reconnectAttempts: number;
}

// ============================================================================
// MESSAGE VALIDATION
// ============================================================================

function isValidAlert(data: unknown): data is Alert {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return typeof d.id === 'string' && typeof d.type === 'string' && typeof d.asset === 'string';
}

function isValidTrade(data: unknown): data is Trade {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.id === 'string' &&
    typeof d.asset === 'string' &&
    typeof d.exchange === 'string' &&
    (d.side === 'buy' || d.side === 'sell')
  );
}

function isValidPriceUpdate(
  data: unknown
): data is { asset: Asset; exchange: string; marketType: 'spot' | 'perp'; price: number } {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.asset === 'string' &&
    typeof d.exchange === 'string' &&
    (d.marketType === 'spot' || d.marketType === 'perp') &&
    typeof d.price === 'number'
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function getWsUrl(path: string): string {
  const wsBase = import.meta.env.VITE_WS_URL;
  if (wsBase) {
    return `${wsBase}${path}`;
  }
  const apiBase = import.meta.env.VITE_API_URL;
  if (apiBase) {
    const url = new URL(apiBase);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}${path}`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}`;
}

function getBackoffDelay(attempt: number): number {
  return Math.min(BACKOFF_BASE * Math.pow(2, attempt), BACKOFF_MAX);
}

// ============================================================================
// CONNECTION MANAGER
// ============================================================================

function createConnection(
  path: string,
  onMessage: (msg: Record<string, unknown>) => void,
  onStatusChange: (status: WsStatus) => void,
  getToken: () => string | null
): WsConnection {
  const url = getWsUrl(path);
  const ws = new WebSocket(url);

  const conn: WsConnection = {
    ws,
    status: 'connecting',
    pingInterval: null,
    reconnectTimeout: null,
    reconnectAttempts: 0,
  };

  onStatusChange('connecting');

  ws.onopen = () => {
    conn.reconnectAttempts = 0;
    // Wait for auth_required before marking connected
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data) as Record<string, unknown>;

      if (msg.type === 'auth_required') {
        conn.status = 'authenticating';
        onStatusChange('authenticating');
        const token = getToken();
        if (token) {
          ws.send(JSON.stringify({ type: 'auth', token }));
        } else {
          // No token — close and don't reconnect
          ws.close(4001, 'No auth token');
          return;
        }
        return;
      }

      if (msg.type === 'auth_ack') {
        conn.status = 'connected';
        onStatusChange('connected');
        // Start pings
        conn.pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);
        return;
      }

      if (msg.type === 'auth_error') {
        conn.status = 'error';
        onStatusChange('error');
        ws.close(4003, 'Auth failed');
        return;
      }

      if (msg.type === 'pong') return;

      if (msg.type === 'init') {
        // Server sends init with recentAlerts + sources after auth_ack
        const initData = msg as Record<string, unknown>;
        const store = useAlertStore.getState();
        if (Array.isArray(initData.recentAlerts)) {
          store.setInitialState({ alerts: initData.recentAlerts as Alert[] });
        }
        if (initData.sources && typeof initData.sources === 'object') {
          store.setSources(initData.sources as Record<string, boolean>);
        }
        onMessage(msg);
        return;
      }

      if (msg.type === 'error') {
        onMessage(msg);
        return;
      }

      // For servers that don't require auth (public endpoints)
      if (msg.type === 'connected' || msg.type === 'state') {
        if (conn.status !== 'connected') {
          conn.status = 'connected';
          onStatusChange('connected');
          if (!conn.pingInterval) {
            conn.pingInterval = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, PING_INTERVAL);
          }
        }
      }

      onMessage(msg);
    } catch {
      // Ignore malformed messages
    }
  };

  ws.onerror = () => {
    conn.status = 'error';
    onStatusChange('error');
  };

  ws.onclose = () => {
    if (conn.pingInterval) {
      clearInterval(conn.pingInterval);
      conn.pingInterval = null;
    }
  };

  return conn;
}

function destroyConnection(conn: WsConnection | null): void {
  if (!conn) return;
  if (conn.pingInterval) {
    clearInterval(conn.pingInterval);
    conn.pingInterval = null;
  }
  if (conn.reconnectTimeout) {
    clearTimeout(conn.reconnectTimeout);
    conn.reconnectTimeout = null;
  }
  if (conn.ws.readyState === WebSocket.OPEN || conn.ws.readyState === WebSocket.CONNECTING) {
    conn.ws.close(1000, 'Client disconnect');
  }
}

// ============================================================================
// HOOK
// ============================================================================

export function useWebSocket() {
  const alertConnRef = useRef<WsConnection | null>(null);
  const assetConnRef = useRef<WsConnection | null>(null);
  const currentAssetRef = useRef<Asset | null>(null);
  const isUnmountedRef = useRef(false);

  // Stable store references via refs
  const storeRef = useRef({
    setConnected: useAlertStore.getState().setConnected,
    setConnecting: useAlertStore.getState().setConnecting,
    setError: useAlertStore.getState().setError,
    setSources: useAlertStore.getState().setSources,
    addAlert: useAlertStore.getState().addAlert,
    addAlerts: useAlertStore.getState().addAlerts,
    addTrade: useAlertStore.getState().addTrade,
    updatePrice: useAlertStore.getState().updatePrice,
    setInitialState: useAlertStore.getState().setInitialState,
  });

  // Keep refs current
  const setConnected = useAlertStore((s) => s.setConnected);
  const setConnecting = useAlertStore((s) => s.setConnecting);
  const setError = useAlertStore((s) => s.setError);
  const setSources = useAlertStore((s) => s.setSources);
  const addAlert = useAlertStore((s) => s.addAlert);
  const addAlerts = useAlertStore((s) => s.addAlerts);
  const addTrade = useAlertStore((s) => s.addTrade);
  const updatePrice = useAlertStore((s) => s.updatePrice);
  const setInitialState = useAlertStore((s) => s.setInitialState);
  const selectedAsset = useAlertStore((s) => s.selectedAsset);

  useEffect(() => {
    storeRef.current = {
      setConnected,
      setConnecting,
      setError,
      setSources,
      addAlert,
      addAlerts,
      addTrade,
      updatePrice,
      setInitialState,
    };
  }, [setConnected, setConnecting, setError, setSources, addAlert, addAlerts, addTrade, updatePrice, setInitialState]);

  const getToken = useCallback((): string | null => {
    return useAuthStore.getState().token;
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((msg: Record<string, unknown>) => {
    const { setInitialState: init, setSources: src, addAlert: aa, addAlerts: aas, addTrade: at, updatePrice: up, setError: se } = storeRef.current;

    switch (msg.type) {
      case 'state':
        if (msg.data && typeof msg.data === 'object') {
          init(msg.data as Parameters<typeof init>[0]);
        }
        break;

      case 'alert':
        if (isValidAlert(msg.data)) {
          aa(msg.data);
        }
        break;

      // Batch alerts from Durable Objects: { type: 'alerts', alerts: [...] }
      case 'alerts': {
        const alertsArr = msg.alerts;
        if (Array.isArray(alertsArr)) {
          const valid = alertsArr.filter(isValidAlert);
          if (valid.length > 0) {
            aas(valid);
          }
        }
        break;
      }

      case 'largeTrade':
      case 'trade':
        if (isValidTrade(msg.data)) {
          at(msg.data);
        }
        break;

      case 'price':
      case 'price_update': {
        if (isValidPriceUpdate(msg.data)) {
          up(msg.data.asset, msg.data.exchange, msg.data.marketType, msg.data.price);
        }
        break;
      }

      case 'sources':
        if (msg.data && typeof msg.data === 'object') {
          src(msg.data as Record<string, boolean>);
        }
        break;

      case 'state_update':
        if (msg.data && typeof msg.data === 'object') {
          init(msg.data as Parameters<typeof init>[0]);
        }
        break;

      case 'error':
        se(String(msg.data || 'Unknown error'));
        break;

      default:
        break;
    }
  }, []);

  // Aggregate status from both connections
  const updateAggregateStatus = useCallback(() => {
    const alertStatus = alertConnRef.current?.status ?? 'idle';
    const assetStatus = assetConnRef.current?.status ?? 'idle';

    // Connected if either connection is connected
    const anyConnected = alertStatus === 'connected' || assetStatus === 'connected';
    const anyConnecting = alertStatus === 'connecting' || assetStatus === 'connecting' || alertStatus === 'authenticating' || assetStatus === 'authenticating';

    storeRef.current.setConnected(anyConnected);
    if (!anyConnected) {
      storeRef.current.setConnecting(anyConnecting);
    }
  }, []);

  // Connect to /ws/alerts (global alerts channel)
  const connectAlerts = useCallback(() => {
    if (isUnmountedRef.current) return;

    destroyConnection(alertConnRef.current);

    const conn = createConnection(
      '/ws/alerts',
      handleMessage,
      () => updateAggregateStatus(),
      getToken
    );

    conn.ws.onclose = (event) => {
      if (conn.pingInterval) {
        clearInterval(conn.pingInterval);
        conn.pingInterval = null;
      }
      if (isUnmountedRef.current || event.code === 1000 || event.code === 4001) return;

      conn.reconnectAttempts++;
      if (conn.reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
        const delay = getBackoffDelay(conn.reconnectAttempts - 1);
        conn.reconnectTimeout = setTimeout(connectAlerts, delay);
      } else {
        storeRef.current.setError(`Alert connection failed after ${MAX_RECONNECT_ATTEMPTS} attempts`);
      }
      updateAggregateStatus();
    };

    alertConnRef.current = conn;
  }, [handleMessage, updateAggregateStatus, getToken]);

  // Connect to /ws/{asset} (per-asset channel)
  const connectAsset = useCallback((asset: Asset) => {
    if (isUnmountedRef.current) return;

    destroyConnection(assetConnRef.current);
    currentAssetRef.current = asset;

    const conn = createConnection(
      `/ws/${asset}`,
      handleMessage,
      () => updateAggregateStatus(),
      getToken
    );

    conn.ws.onclose = (event) => {
      if (conn.pingInterval) {
        clearInterval(conn.pingInterval);
        conn.pingInterval = null;
      }
      if (isUnmountedRef.current || event.code === 1000 || event.code === 4001) return;
      // Only reconnect if this is still the selected asset
      if (currentAssetRef.current !== asset) return;

      conn.reconnectAttempts++;
      if (conn.reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
        const delay = getBackoffDelay(conn.reconnectAttempts - 1);
        conn.reconnectTimeout = setTimeout(() => connectAsset(asset), delay);
      }
      updateAggregateStatus();
    };

    assetConnRef.current = conn;
  }, [handleMessage, updateAggregateStatus, getToken]);

  // Initial connection
  useEffect(() => {
    isUnmountedRef.current = false;
    storeRef.current.setConnecting(true);
    storeRef.current.setError(null);

    connectAlerts();
    connectAsset(selectedAsset);

    return () => {
      isUnmountedRef.current = true;
      destroyConnection(alertConnRef.current);
      destroyConnection(assetConnRef.current);
      alertConnRef.current = null;
      assetConnRef.current = null;
      storeRef.current.setConnected(false);
    };
    // Only run on mount/unmount — asset switching handled separately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to selectedAsset changes
  useEffect(() => {
    if (currentAssetRef.current === selectedAsset) return;
    connectAsset(selectedAsset);
  }, [selectedAsset, connectAsset]);

  const send = useCallback((message: object) => {
    // Send to asset connection (primary data channel)
    if (assetConnRef.current?.ws.readyState === WebSocket.OPEN) {
      assetConnRef.current.ws.send(JSON.stringify(message));
    }
  }, []);

  return { send };
}
